<template>
    <v-row justify="center">
        <v-dialog v-model="show_company_dialog" max-width="1800" persistent scrollable>
            <v-card>
                <v-card-title>Company</v-card-title>
                <v-card-text class="pt-3">
                    <v-row>
                        <v-col cols="5" class="pr-5">
                            <v-text-field v-model="company.company_name" label="Name" outlined dense></v-text-field>
                            <v-text-field v-model="company.brand_name" label="Brand Name"
                                hint="Used in email sign-offs. Leave blank to use the company name." persistent-hint
                                outlined dense class="mb-3"></v-text-field>
                            <v-file-input v-model="file" label="Upload New Logo" outlined dense></v-file-input>
                            <v-img contain :src="api_url + company.company_logo"></v-img>
                            <v-divider></v-divider>
                            <v-textarea v-model="company_details.description" label="Description" outlined dense
                                class="mt-3"></v-textarea>
                            <v-text-field v-model="company_details.sub_description" label="Sub Description" outlined dense></v-text-field>
                        </v-col>
                        <v-col cols="4" class="px-5">
                            <v-text-field v-model="company.email" label="Email" outlined dense></v-text-field>
                            <v-text-field v-model="company.number" label="Number" outlined dense></v-text-field>
                            <v-text-field v-model="company.phone_note" label="Number Note"
                                hint="Shown after the number on itineraries, e.g. (option 2)." persistent-hint outlined
                                dense class="mb-3"></v-text-field>
                            <v-text-field v-model="company.address" label="Address" outlined dense></v-text-field>
                            <v-text-field v-model="company.website" label="Website" outlined dense></v-text-field>
                            <v-text-field v-model="company.terms_url" label="Terms and Conditions URL" outlined
                                dense></v-text-field>
                            <v-text-field v-model="company.bookings_email" label="Bookings Email"
                                hint="Sender for payment confirmations and booking emails, and where supplier responses are sent."
                                persistent-hint outlined dense class="mb-3"></v-text-field>
                            <v-text-field v-model="company.events_email" label="Events Email"
                                hint="Copied and used as reply-to on booking emails." persistent-hint outlined dense
                                class="mb-3"></v-text-field>
                            <v-text-field v-model="company.history_email" label="History Email"
                                hint="BCC'd on emails sent to customers, so there is a copy on file." persistent-hint
                                outlined dense class="mb-3"></v-text-field>
                            <v-text-field v-model="company.password" label="Company Password" outlined
                                dense></v-text-field>
                            <v-text-field v-model="company.cutoff_duration"
                                label="Cutoff For Payment (how many days before event date)" outlined
                                hint="Sets the pay-by date (balance due) on new events: this many days before the event, moved to the nearest Friday. Existing events keep their date. Also sets the &quot;balance due X weeks before&quot; wording in quote, organiser and invitee emails."
                                persistent-hint dense class="mb-3"></v-text-field>
                            <v-text-field v-model="company.admin_fee" label="Admin Fee" type="number" outlined
                                dense></v-text-field>
                            <v-text-field v-model="company.late_addition_fee" label="Late Addition Fee" type="number"
                                hint="Fee quoted on itineraries for adding someone last minute. Leave blank for none."
                                persistent-hint outlined dense class="mb-3"></v-text-field>
                            <v-text-field v-model="company.minimum_quote_profit" label="Minimum Quote Profit"
                                type="number" min="0"
                                hint="Profit per person a quote must make when its location has no profit margin set. Leave blank to not check those quotes."
                                persistent-hint outlined dense class="mb-3"></v-text-field>
                            <div class="mb-3">Late Fee</div>
                            <div style="display:flex;">
                                <v-text-field v-model="company.late_fee_amount" label="Amount" outlined dense
                                    class="mr-2"></v-text-field>
                                <v-text-field v-model="company.late_fee_days" label="Days" outlined dense
                                    class="mr-2"></v-text-field>
                                <v-select v-model="company.late_fee_date_type" label="Date type"
                                    :items="['Event Date', 'Pay By Date']" outlined dense></v-select>
                            </div>
                            <div class="caption grey--text mb-3">Charged per invitee on full payments made within this
                                many days of the date. Amount 0 turns it off. New events copy these values.</div>
                            <v-switch v-model="company.xero_enabled" label="Send payments to Xero" dense
                                hint="Each card or manually recorded payment becomes a Xero invoice and payment, as on my-stagit. Xero must be connected at the API's /xero/connect."
                                persistent-hint class="mt-0 mb-3"></v-switch>
                        </v-col>
                        <v-col cols="3" class="pl-5">
                            <v-text-field v-model="company.created_at" label="Created At" outlined dense
                                disabled></v-text-field>
                            <v-text-field v-model="company.created_by" label="Created By" outlined dense
                                disabled></v-text-field>
                            <v-text-field v-model="company.currency" label="Currency" outlined dense
                                disabled></v-text-field>
                            <v-text-field v-model="company.timezone" label="Timezone" outlined dense
                                disabled></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="custom-buttons" text @click="closeCompanyDialog()">Close</v-btn>
                    <v-btn dark class="custom-buttons" @click="updateCompany()">Update</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    components: {
    },
    props: [
        'show_company_dialog'
    ],
    data: () => ({
        classes: {
        },
        selected_template: null,
        company: {
            id: null,
            company_name: null,
            brand_name: null,
            company_logo: null,
            email: null,
            bookings_email: null,
            events_email: null,
            history_email: null,
            number: null,
            phone_note: null,
            late_addition_fee: null,
            minimum_quote_profit: null,
            address: null,
            website: null,
            terms_url: null,
            password: null,
            currency: null,
            timezone: null,
            cutoff_duration: null,
            admin_fee: 0,
            late_fee_amount: 0,
            late_fee_days: 0,
            late_fee_date_type: null,
            xero_enabled: false,
            created_at: null,
            created_by: null,
        },
        file: null,
        api_url: null,
        company_details:{
            company_id: null,
            id: null,
            description: null,
            sub_description: null
        }
    }),
    created() {
    },
    async mounted() {
        this.setCompany()
        this.api_url = process.env.VUE_APP_API_URL
    },
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
            get_user: 'auth/get_user',
        })
    },
    methods: {
        setCompany() {
            this.company.id = this.get_selected_company.id
            this.company.company_name = this.get_selected_company.company_name
            this.company.company_logo = this.get_selected_company.company_logo
            this.company.brand_name = this.get_selected_company.brand_name
            this.company.email = this.get_selected_company.email
            this.company.bookings_email = this.get_selected_company.bookings_email
            this.company.events_email = this.get_selected_company.events_email
            this.company.history_email = this.get_selected_company.history_email
            this.company.number = this.get_selected_company.number
            this.company.phone_note = this.get_selected_company.phone_note
            this.company.late_addition_fee = this.get_selected_company.late_addition_fee
            this.company.minimum_quote_profit = this.get_selected_company.minimum_quote_profit
            this.company.address = this.get_selected_company.address
            this.company.website = this.get_selected_company.website
            this.company.terms_url = this.get_selected_company.terms_url
            this.company.password = this.get_selected_company.password
            this.company.currency = this.get_selected_company.currency
            this.company.timezone = this.get_selected_company.timezone
            this.company.cutoff_duration = this.get_selected_company.cutoff_duration
            this.company.admin_fee = this.get_selected_company.admin_fee
            this.company.late_fee_amount = this.get_selected_company.late_fee_amount
            this.company.late_fee_days = this.get_selected_company.late_fee_days
            this.company.late_fee_date_type = this.get_selected_company.late_fee_date_type
            this.company.xero_enabled = !!this.get_selected_company.xero_enabled
            this.company.created_at = this.$date(this.get_selected_company.created_at).format('MMMM DD, YYYY')
            this.company.created_by = this.get_selected_company.created_by.name

            this.company_details.company_id = this.get_selected_company.id
            if ( this.get_selected_company.company_details != null ) {
                this.company_details.id = this.get_selected_company.company_details.id
                this.company_details.description = this.get_selected_company.company_details.description
                this.company_details.sub_description = this.get_selected_company.company_details.sub_description
            } 
        },
        closeCompanyDialog() {
            this.$emit('closeCompanyDialog')
            this.file = null
        },
        async updateCompany() {
            if (this.company.company_name == null || this.company.company_name == '') {
                this.$toast.error('Please enter a company name')
                return
            }
            const formData = new FormData()
            formData.append('company_logo_file', this.file)
            formData.append('id', this.company.id)
            formData.append('company_name', this.company.company_name)
            formData.append('email', this.company.email)
            formData.append('number', this.company.number)
            formData.append('address', this.company.address)
            formData.append('website', this.company.website)
            // Blank rather than "null": FormData would send the string "null" for an unset field.
            formData.append('brand_name', this.company.brand_name ?? '')
            formData.append('terms_url', this.company.terms_url ?? '')
            formData.append('phone_note', this.company.phone_note ?? '')
            formData.append('late_addition_fee', this.company.late_addition_fee ?? '')
            formData.append('minimum_quote_profit', this.company.minimum_quote_profit ?? '')
            formData.append('bookings_email', this.company.bookings_email ?? '')
            formData.append('events_email', this.company.events_email ?? '')
            formData.append('history_email', this.company.history_email ?? '')
            formData.append('password', this.company.password)
            formData.append('cutoff_duration', this.company.cutoff_duration)
            formData.append('admin_fee', this.company.admin_fee)
            formData.append('late_fee_amount', this.company.late_fee_amount)
            formData.append('late_fee_days', this.company.late_fee_days)
            formData.append('late_fee_date_type', this.company.late_fee_date_type)
            formData.append('xero_enabled', this.company.xero_enabled ? 1 : 0)
            const response = await this.$axios.post(`admin/companies/update_company/${this.company.id}`, formData)
            const result = response.data
            if (result.response == true) {
                this.$toast.success(result.message)
                this.$store.dispatch('auth/set_selected_company', result.data)
                this.setCompany()
                this.closeCompanyDialog()
            }

            const detailsResponse = await this.$axios.post('admin/company_settings/update_company_details', this.company_details)
            const results = detailsResponse.data
            if ( results.response == true ) {
                this.$toast.success(results.message)
                this.$store.dispatch('auth/set_selected_company', results.data)
                this.setCompany()
            }

        }
    },
    watch: {
        // The stored selected company can be older than the database (e.g. xero_enabled turned on by a
        // migration showed as off), and saving would write the stale values back: reload it on open
        async show_company_dialog(open) {
            if (!open) return
            const result = (await this.$axios.get(`admin/companies/get_company/${this.get_selected_company.id}`)).data
            if (result.response == true && result.data) {
                this.$store.dispatch('auth/set_selected_company', result.data)
                this.setCompany()
            }
        }
    }
}
</script>

<style scoped lang="scss">
:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: normal;
    box-shadow: none !important;
}

:deep(.custom-buttons button) {
    padding: 8px 7px 8px 7px !important;
    border-radius: 5px;
}

:deep(.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row)) {
    border-bottom: none !important;
}

::-webkit-scrollbar {
    width: 7px;
    background: white;
}

::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: #D9D9D9;
    color: #D9D9D9;
    height: 50px;
}
</style>